import { dateToString } from 'modules/core/lib/dates'

import useRowAndEmployeeSelection from 'modules/shift_management/hooks/useRowAndEmployeeSelection'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type CommonSelectors from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import { Leave } from 'modules/shift_management/lib/types'

import useDateRangeSelection from './useDateRangeSelection'

type DaysByRow = Record<string, string[]>

const useDefaultValues = (
  employeeIds: number[],
  commonSelectors?: CommonSelectors | null | undefined,
  date?: moment.Moment,
  leave?: Leave
) => {
  const [{ view }] = useSchedule()
  const selectedDate = date && dateToString(date, 'backend')
  const filterStartDate = commonSelectors?.get('start_date').value
  const filterEndDate = commonSelectors?.get('end_date').value

  const startDate = view.isByAreas && selectedDate ? selectedDate : filterStartDate
  const singleDate = commonSelectors?.doesNotRepeat ? startDate : null
  const endDate = view.isByAreas && selectedDate ? selectedDate : filterEndDate

  const weekDays = commonSelectors?.isCustomPeriod
    ? commonSelectors?.get('apply_on_days').value
    : null

  const { daysByRow, isEnabled: hasSelection } = useSelectionState()

  // Used if there is no selection and the date range selector is visible (view.isByEmployees)
  const calculatedDaysByEmployee = useDateRangeSelection(
    employeeIds,
    startDate,
    singleDate || endDate,
    weekDays,
    false,
    date
  )

  const daysByEmployeeAndRow = useRowAndEmployeeSelection(daysByRow, employeeIds)

  const selectedDaysByView = view.isByEmployees ? daysByRow : daysByEmployeeAndRow

  // if there is a leave and view is not weekly, we use the selected day
  // since visually the leave is shown individually per day
  // otherwise the leave range will be used with the startDate and endDate
  // in calculatedDaysByEmployee
  const useSelectionForLeave = leave && !view.isWeekly

  const daysByEmployee: DaysByRow =
    (hasSelection && !leave) || useSelectionForLeave ? selectedDaysByView : calculatedDaysByEmployee

  return {
    weekDays,
    daysByEmployee,
  }
}

export default useDefaultValues
